import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiX } from "react-icons/fi";
import { useUserDirectory } from "../../../../hooks/useUserDirectory";

// Rows rendered at once; narrowing the search shows the rest.
const SHOWN_LIMIT = 200;

// Find anyone in the user directory (useUserDirectory), in one of two modes:
//
//   onPick(uid)          pick one person - Usage & Billing opens their usage,
//                        including people who haven't used AI yet.
//   multiple + onConfirm tick several and confirm them as one batch - the
//                        Groups tab's "Add members". onConfirm(uids) resolves
//                        to { failed: [{ userId, error }] }; the failures stay
//                        ticked so they can be retried, and the picker closes
//                        when nothing failed.
//
// hideGroupId leaves out people already in that group.
export default function UserPickerModal({
  onPick,
  multiple = false,
  onConfirm,
  hideGroupId,
  title,
  subtitle,
  confirmLabel,
  onClose,
  language,
}) {
  const zh = language === "zh";
  const directory = useUserDirectory();
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, busy]);

  const groupName = (id) =>
    directory.groups.find((g) => g.id === id)?.name ||
    id ||
    (zh ? "無群組" : "No group");

  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      directory.users.filter(
        (u) =>
          (!hideGroupId || u.groupId !== hideGroupId) &&
          (groupFilter === "all" || u.groupId === groupFilter) &&
          (!q ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.displayName || "").toLowerCase().includes(q)),
      ),
    [directory.users, hideGroupId, groupFilter, q],
  );
  const shown = matches.slice(0, SHOWN_LIMIT);
  const allShownSelected =
    shown.length > 0 && shown.every((u) => selected.has(u.id));

  const toggle = (uid) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  const toggleShown = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      shown.forEach((u) =>
        allShownSelected ? next.delete(u.id) : next.add(u.id),
      );
      return next;
    });

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const { failed = [] } = await onConfirm([...selected]);
      if (failed.length === 0) {
        onClose();
        return;
      }
      setSelected(new Set(failed.map((f) => f.userId)));
      setError(
        zh
          ? `${failed.length} 位未能完成：${failed[0].error}`
          : `${failed.length} couldn't be done: ${failed[0].error}`,
      );
      // The ones that went through may have changed group.
      directory.reload();
    } finally {
      setBusy(false);
    }
  };

  const th =
    "sticky top-0 z-10 px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600";

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-picker-title"
        className={`w-full ${multiple ? "max-w-3xl" : "max-w-xl"} max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-scaleIn`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2
              id="user-picker-title"
              className="text-lg font-bold text-gray-900 dark:text-white"
            >
              {title || (zh ? "選擇使用者" : "Choose a user")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {subtitle ||
                (zh
                  ? "包含還沒有使用過 AI 的人。"
                  : "Including people who haven't used AI yet.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
            aria-label={zh ? "關閉" : "Close"}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="relative flex-1">
            <FiSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={zh ? "搜尋電子郵件或姓名" : "Search email or name"}
              className="w-full border border-gray-200 dark:border-gray-600 rounded-lg pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
              autoFocus
            />
          </div>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
            aria-label={zh ? "依目前群組篩選" : "Filter by current group"}
          >
            <option value="all">{zh ? "所有群組" : "All groups"}</option>
            {directory.groups
              .filter((g) => g.id !== hideGroupId)
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name || g.id}
                </option>
              ))}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          {directory.status === "error" && (
            <p className="m-5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              {zh ? "無法載入使用者名單：" : "Couldn't load the user list: "}
              {directory.error}
            </p>
          )}
          {directory.status === "loading" && (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
            </div>
          )}
          {directory.status === "ok" && matches.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              {zh ? "沒有符合的使用者。" : "No users match."}
            </p>
          )}
          {directory.status === "ok" && matches.length > 0 && (
            <table className="min-w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  {multiple && (
                    <th className={`${th} w-10`}>
                      <input
                        type="checkbox"
                        checked={allShownSelected}
                        onChange={toggleShown}
                        className="w-4 h-4 accent-orange-500"
                        aria-label={zh ? "全選顯示的使用者" : "Select everyone shown"}
                      />
                    </th>
                  )}
                  <th className={th}>{zh ? "姓名" : "Name"}</th>
                  <th className={th}>{zh ? "電子郵件" : "Email"}</th>
                  <th className={th}>{zh ? "目前群組" : "Current group"}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((user) => {
                  const checked = selected.has(user.id);
                  return (
                    <tr
                      key={user.id}
                      onClick={() =>
                        multiple ? toggle(user.id) : onPick(user.id)
                      }
                      className={`cursor-pointer ${
                        checked
                          ? "bg-orange-50 dark:bg-orange-900/20"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/60"
                      }`}
                    >
                      {multiple && (
                        <td className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(user.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-orange-500"
                            aria-label={user.email || user.id}
                          />
                        </td>
                      )}
                      <td className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 text-sm font-medium text-gray-900 dark:text-white">
                        {user.displayName || (
                          <span className="font-normal text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 break-all">
                        {user.email || user.id}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {groupName(user.groupId)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {directory.status === "ok" &&
          (matches.length > SHOWN_LIMIT || directory.hasNext) && (
            <p className="px-5 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              {matches.length > SHOWN_LIMIT &&
                (zh
                  ? `顯示前 ${SHOWN_LIMIT} 位，共 ${matches.length} 位符合，請縮小搜尋範圍。`
                  : `Showing ${SHOWN_LIMIT} of ${matches.length} matches; narrow the search. `)}
              {directory.hasNext &&
                (zh
                  ? `只載入了依電子郵件排序的前 ${directory.limit} 位使用者。`
                  : `Only the first ${directory.limit} users by email were loaded.`)}
            </p>
          )}

        {multiple && (
          <div className="flex flex-col gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 rounded-b-2xl">
            {error && (
              <p className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {zh ? `已選 ${selected.size} 位` : `${selected.size} selected`}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  {zh ? "取消" : "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={confirm}
                  disabled={busy || selected.size === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy && (
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  )}
                  {confirmLabel
                    ? confirmLabel(selected.size)
                    : zh
                      ? `確認 ${selected.size} 位`
                      : `Confirm ${selected.size}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
